import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {StateService} from "../../../services/StateService";
import {SwitchBoard} from "../../../services/SwitchBoard";
import {Router} from "@angular/router";
import {PageTitleService} from "../../../services/PageTitle";
import {takeUntil} from "rxjs/operators";
import {Session} from "../../../services/Session";
import {CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {FormatAmountPipe} from "../../../pipes/format-amount.pipe";
import {ManageTaxStrategiesServices} from "../../../tpiq-services/manage-tax-strategies.service";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import * as bootstrap from 'bootstrap';

declare const _: any;

@Component({
  selector: 'app-tax-strategies-info',
  standalone: true,
  imports: [CommonModule, FormsModule, FormatAmountPipe, DragDropModule, TpiqSelectDropdownComponent],
  templateUrl: './tax-strategies-info.component.html',
  styleUrl: './tax-strategies-info.component.scss'
})
export class TaxStrategiesInfoComponent implements OnInit, OnChanges, OnDestroy {
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  @Output() onItemAddedToSelectedList: EventEmitter<any> = new EventEmitter<any>();
  @Input() strategies: any;
  @Input() selectedStrategies: any;
  @Input() aiSuggestedStrategies: any;
  @Input() ocrStatus: any;
  @Input() plan: any;
  @Input() states: any;
  @Input() forceAITab: boolean = false; // Only set to true in open-in-RAP scenario
  currentTab = 'AI Strategies';
  @ViewChild('strategiesInfo', { static: true }) strategiesInfo!: TemplateRef<any>;
  searchKey: string;
  allStrategiesData: any;
  aiStrategies: any;
  showFilters = false;
  filterModel: any = {
    category: [],
    complexity: [],
    urgency: [],
    riskLevel: [],
    burden: [],
    amountType: [],
    applicableTo: '',
    requiresInvestment: '',
    tagId: []
  };
  categoryOptions: { value: string; label: string }[] = [];
  complexityOptions = [
    { value: '1', label: '1 - Single Input' },
    { value: '2', label: '2 - Multiple Inputs' },
    { value: '3', label: '3 - Conditional' },
    { value: '4', label: '4 - Complex' },
    { value: '5', label: '5 - Specialized' }
  ];
  urgencyOptions = [
    { value: 'No deadline', label: 'No deadline' },
    { value: 'Annual', label: 'Annual' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Weeks', label: 'Weeks' },
    { value: 'Days', label: 'Days' }
  ];
  riskOptions = [
    { value: 'Sustained', label: '1 - Minimal' },
    { value: 'More Likely Than Not', label: '2 - Low' },
    { value: 'Substantial Authority', label: '3 - Moderate' },
    { value: 'Reasonable Basis', label: '4 - Heightened' },
    { value: 'Listed/Reportable', label: '5 - Significant' }
  ];
  burdenOptions = [
    { value: 'Referral only', label: '1 - Mostly the client' },
    { value: 'Light coordination', label: '2 - Client with advisor help' },
    { value: 'Shared work', label: '3 - Half client/advisor' },
    { value: 'Mostly advisor', label: '4 - Mostly tax advisor' },
    { value: 'All advisor (no vendor)', label: '5 - Concierge' }
  ];
  amountTypeOptions = [
    'Credit', 'Deduction', 'Deduction/Credit', 'Deduction/Deferral', 'Deduction/Exclusion',
    'Deduction/Shifting', 'Deferral', 'Exclusion', 'Exclusion/Shifting', 'Shifting'
  ].map(v => ({ value: v, label: v }));
  applicableToOptions = [
    { value: 'Business', label: 'Business' },
    { value: 'Individual', label: 'Individual' },
    { value: 'Both', label: 'Both' }
  ];
  requiresInvestmentOptions = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' }
  ];
  tagOptions: any[] = [];
  filterAvailability = {
    category: false,
    complexity: false,
    amountType: false,
    applicableTo: false,
    requiresInvestment: false,
    tags: false,
    urgency: false,
    riskLevel: false,
    burden: false
  };
  multiSelectFilterKeys: string[] = ['category', 'complexity', 'urgency', 'riskLevel', 'burden', 'amountType', 'tagId'];
  multiSelectDropdownState: any = {
    category: false,
    complexity: false,
    urgency: false,
    riskLevel: false,
    burden: false,
    amountType: false,
    tagId: false
  };
  private searchDebounceTimer: any;

  ngAfterViewInit() {
      setTimeout(() =>this.initTooltips(),2000);
    }

  private initTooltips() {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((el: any) => {
      const existingTooltip = bootstrap.Tooltip.getInstance(el);
      if (existingTooltip) {
        existingTooltip.dispose();
      }
      new bootstrap.Tooltip(el);
    });
  }
  

  constructor(
    private _router: Router,
    private titleService: PageTitleService,
    private stateService: StateService,
    private switchBoard: SwitchBoard,
    private viewContainerRef: ViewContainerRef,
    private taxStrategyServices: ManageTaxStrategiesServices
  ) {}

  ngOnInit(): void {
    this.viewContainerRef.createEmbeddedView(this.strategiesInfo);
    this.titleService.setPageTitle('');
    this.user = Session.getUser();
    // In open-in-RAP scenario (forceAITab=true), always show AI Strategies tab
    if (this.forceAITab) {
      // Always default to AI Strategies in open-in-RAP, even if data is still loading
      this.currentTab = 'AI Strategies';
    } else if (!this.ocrStatus || this.ocrStatus === 'Error' || !this.aiSuggestedStrategies || this.aiSuggestedStrategies.length === 0) {
      this.currentTab = 'All Strategies';
    } else {
      this.currentTab = 'AI Strategies';
    }
    this.allStrategiesData = (this.strategies && this.strategies.length > 0) ? _.cloneDeep(this.strategies) : [];

    if (this.allStrategiesData && this.allStrategiesData.length > 0) {
      _.each(this.allStrategiesData, (strategy) => {
        strategy.showFullDescription = false;
      });
    }

    this.aiStrategies = (this.aiSuggestedStrategies && this.aiSuggestedStrategies.length > 0) ? _.cloneDeep(this.aiSuggestedStrategies) : [];
    if (this.aiStrategies && this.aiStrategies.length > 0) {
      _.each(this.aiStrategies, (strategy) => {
        strategy.showFullDescription = false;
        _.remove(this.allStrategiesData, {id: strategy.id});
        strategy.suggestedInfo = strategy.suggestedInfo.split("$$state$$").join(this.getStateName());
        strategy.suggestedInfo = strategy.suggestedInfo.split("0 state").join(this.getStateName());
      });
    }
    // Removing selected suggestions from AI suggestions and All strategies
    if (this.selectedStrategies && this.selectedStrategies.length > 0){
      _.each(this.selectedStrategies, (strategy) => {
        strategy.showFullDescription = false;
        let obj = _.find(this.aiStrategies, {id: strategy.id});
        if (obj) {
          _.remove(this.aiStrategies, {id: strategy.id});
        }
        let strObj = _.find(this.allStrategiesData, {id: strategy.id});
        if (strObj) {
          _.remove(this.allStrategiesData, {id: strategy.id});
        }
      });
    }
    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(obj => {
      const prevState = this.stateService.getPrevState();
      if (prevState) {
        this.stateService.pop();
        this._router.navigate([prevState.url]);
      }
    });

    this.taxStrategyServices.getTagsDataForFirm(this.org, this.user?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.tagOptions = _.uniqBy(res || [], 'label');
          this.updateFilterAvailability();
        },
        error: err => {
          console.error('Failed to load tax-strategy tag options', err);
          this.tagOptions = [];
          this.updateFilterAvailability();
        }
      });

    this.updateFilterAvailability();

    // Defensive: if the component instance is reused (route reuse, *ngIf
    // toggle that keeps state), search/filter state may survive while
    // ngOnInit rebuilds from inputs. Re-apply so the visible list matches.
    if (this.searchKey || this.hasActiveFilters()) {
      this.filterData();
    }
  }

  private updateFilterAvailability() {
    const strategies = Array.isArray(this.strategies) ? this.strategies : [];
    const categories: string[] = _.uniq(_.compact(_.map(strategies, (s: any) => s && s.type)));
    this.categoryOptions = (categories as string[])
      .slice()
      .sort((a, b) => a.localeCompare(b))
      .map((c: string) => ({ value: c, label: c }));
    const has = (keys: string[]) => _.some(strategies, (s: any) => !!this.getProp(s, keys));

    this.filterAvailability.category = this.categoryOptions.length > 0;
    this.filterAvailability.complexity = has(['complexity']);
    // Allow URB filters to remain selectable even if current dataset lacks values,
    // so users can pre-filter future loads. Gate on actual presence when available,
    // but include alternate API field names (risk_level) for robustness.
    this.filterAvailability.urgency = has(['urgency']) || (Array.isArray(this.urgencyOptions) && this.urgencyOptions.length > 0);
    this.filterAvailability.riskLevel = has(['riskLevel', 'risk_level', 'risk']) || (Array.isArray(this.riskOptions) && this.riskOptions.length > 0);
    this.filterAvailability.burden = has(['burden']) || (Array.isArray(this.burdenOptions) && this.burdenOptions.length > 0);
    this.filterAvailability.amountType = has(['amountType']);
    this.filterAvailability.applicableTo = has(['applicableTo']);
    this.filterAvailability.requiresInvestment = _.some(strategies, (s: any) =>
      s && ('isInvestmentEnabled' in s || 'investmentEnabled' in s)
    );
    this.filterAvailability.tags = Array.isArray(this.tagOptions) && this.tagOptions.length > 0;
  }

  hasAnyFilterAvailable(): boolean {
    return this.filterAvailability.category ||
           this.filterAvailability.complexity ||
           this.filterAvailability.urgency ||
           this.filterAvailability.riskLevel ||
           this.filterAvailability.burden ||
           this.filterAvailability.amountType ||
           this.filterAvailability.applicableTo ||
           this.filterAvailability.requiresInvestment ||
           this.filterAvailability.tags;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['strategies']) {
      this.updateFilterAvailability();
      if (!changes['strategies'].firstChange) {
        this.filterData();
      }
    }
    // Detect when aiSuggestedStrategies, ocrStatus, or forceAITab changes and update the tab
    if (changes['aiSuggestedStrategies'] || changes['ocrStatus'] || changes['forceAITab']) {
      // In open-in-RAP scenario (forceAITab=true), always show AI Strategies tab
      // This ensures the tab is set correctly when data loads after skip action
      if (this.forceAITab) {
        // If we have suggestions, show AI Strategies tab
        if (this.aiSuggestedStrategies && this.aiSuggestedStrategies.length > 0) {
          this.currentTab = 'AI Strategies';
        } else {
          // Even if no suggestions yet, default to AI Strategies in open-in-RAP
          // (data might be loading, component will update when data arrives)
          this.currentTab = 'AI Strategies';
        }
      } else if (!this.ocrStatus || this.ocrStatus === 'Error' || !this.aiSuggestedStrategies || this.aiSuggestedStrategies.length === 0) {
        this.currentTab = 'All Strategies';
      } else {
        this.currentTab = 'AI Strategies';
      }
      
      // Re-process the strategies data when aiSuggestedStrategies changes
      if (changes['aiSuggestedStrategies'] && this.aiSuggestedStrategies) {
        this.aiStrategies = (this.aiSuggestedStrategies && this.aiSuggestedStrategies.length > 0) ? _.cloneDeep(this.aiSuggestedStrategies) : [];
        if (this.aiStrategies && this.aiStrategies.length > 0) {
          const stateName = this.plan && this.plan.homeState ? this.getStateName() : '';
          _.each(this.aiStrategies, (strategy) => {
            strategy.showFullDescription = false;
            _.remove(this.allStrategiesData, {id: strategy.id});
            if (strategy.suggestedInfo) {
              strategy.suggestedInfo = strategy.suggestedInfo.split("$$state$$").join(stateName);
              strategy.suggestedInfo = strategy.suggestedInfo.split("0 state").join(stateName);
            }
          });
        }
        // Removing selected suggestions from AI suggestions
        if (this.selectedStrategies && this.selectedStrategies.length > 0){
          _.each(this.selectedStrategies, (strategy) => {
            let obj = _.find(this.aiStrategies, {id: strategy.id});
            if (obj) {
              _.remove(this.aiStrategies, {id: strategy.id});
            }
          });
        }
        // Re-apply active search/filters so an input update from the parent
        // doesn't silently replace the filtered view with the full list.
        if (this.searchKey || this.hasActiveFilters()) {
          this.filterData();
        }
      }
    }
  }

stripHtml(html: string): string {
  if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
}

  getStateName() {
    let stateObj = _.find(this.states, {short: this.plan.homeState});
    return stateObj ? stateObj.name : '';
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    this.viewContainerRef.clear();
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  moveToRight(strategy) {
    if (this.currentTab === 'All Strategies') {
      let obj = _.find(this.allStrategiesData, {id: strategy.id});
      if (obj) {
        this.selectedStrategies.push(obj);
        this.selectedStrategies = _.uniqBy(this.selectedStrategies, 'id');
        this.onItemAddedToSelectedList.emit({list: this.selectedStrategies});
        _.remove(this.allStrategiesData, {id: strategy.id});
      }
    } else if (this.currentTab === 'AI Strategies') {
      let obj = _.find(this.aiStrategies, {id: strategy.id});
      if (obj) {
        this.selectedStrategies.push(obj);
        this.selectedStrategies = _.uniqBy(this.selectedStrategies, 'id');
        this.onItemAddedToSelectedList.emit({list: this.selectedStrategies});
        _.remove(this.aiStrategies, {id: strategy.id});
      }
    }
    // Re-source the left-hand list through current search/filter so we don't
    // leak previously-filtered items back on the next filterData() call.
    this.filterData();
  }

  moveToLeft(strategy) {
    if (strategy.aiSuggested) {
      let obj = _.find(this.selectedStrategies, {id: strategy.id});
      if (obj) {
        _.remove(this.selectedStrategies, {id: strategy.id});
        this.onItemAddedToSelectedList.emit({
          action: 'removed',
          item: obj,
          list: this.selectedStrategies
        });
      }
    } else {
      let obj = _.find(this.selectedStrategies, {id: strategy.id});
      if (obj) {
        _.remove(this.selectedStrategies, {id: strategy.id});
        this.onItemAddedToSelectedList.emit({
          action: 'removed',
          item: obj,
          list: this.selectedStrategies
        });
      }
    }
    // filterData re-sources from the @Input arrays minus selectedStrategies,
    // so the returned item shows up in the correct tab AND respects filters.
    this.filterData();
  }

  selectTab(tab) {
    this.currentTab = tab;
    this.filterData();
    setTimeout(() => this.initTooltips());
  }

  onSearchChange() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.searchDebounceTimer = null;
      this.filterData();
    }, 200);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.showFilters) {
      this.showFilters = false;
      this.closeAllMultiSelectDropdowns();
    }
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.closeAllMultiSelectDropdowns();
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
    if (!this.showFilters) {
      this.closeAllMultiSelectDropdowns();
    }
  }

  applyFilters() {
    this.showFilters = false;
    this.closeAllMultiSelectDropdowns();
    this.filterData();
  }

  clearFilters() {
    this.filterModel = {
      category: [],
      complexity: [],
      urgency: [],
      riskLevel: [],
      burden: [],
      amountType: [],
      applicableTo: '',
      requiresInvestment: '',
      tagId: []
    };
    this.closeAllMultiSelectDropdowns();
    // Re-apply filters (now all client-side, including tags)
    this.filterData();
  }

  hasActiveFilters(): boolean {
    return !!(
      (this.getMultiSelectValues('category').length && this.filterAvailability.category) ||
      (this.getMultiSelectValues('complexity').length && this.filterAvailability.complexity) ||
      (this.getMultiSelectValues('urgency').length && this.filterAvailability.urgency) ||
      (this.getMultiSelectValues('riskLevel').length && this.filterAvailability.riskLevel) ||
      (this.getMultiSelectValues('burden').length && this.filterAvailability.burden) ||
      (this.getMultiSelectValues('amountType').length && this.filterAvailability.amountType) ||
      (this.filterModel.applicableTo && this.filterAvailability.applicableTo) ||
      (this.filterModel.requiresInvestment && this.filterAvailability.requiresInvestment) ||
      (this.getMultiSelectValues('tagId').length && this.filterAvailability.tags)
    );
  }

  onFilterSelect(field: string, valueKey: string, event: any) {
    this.filterModel[field] = (event && event[valueKey] != null) ? event[valueKey] : '';
  }

  isMultiSelectEnabled(filterKey: string): boolean {
    if (filterKey === 'tagId') {
      return !!this.filterAvailability.tags;
    }
    return !!this.filterAvailability[filterKey];
  }

  toggleMultiSelect($event: any, key: string) {
    $event.stopImmediatePropagation();
    if (!this.isMultiSelectEnabled(key)) {
      return;
    }
    const nextValue = !this.multiSelectDropdownState[key];
    this.closeAllMultiSelectDropdowns();
    this.multiSelectDropdownState[key] = nextValue;
  }

  closeAllMultiSelectDropdowns() {
    _.forEach(this.multiSelectFilterKeys, (key) => {
      this.multiSelectDropdownState[key] = false;
    });
  }

  isMultiSelectOpen(key: string): boolean {
    return !!this.multiSelectDropdownState[key];
  }

  getOptionsByFilterKey(key: string): Array<any> {
    const map: any = {
      category: this.categoryOptions,
      complexity: this.complexityOptions,
      urgency: this.urgencyOptions,
      riskLevel: this.riskOptions,
      burden: this.burdenOptions,
      amountType: this.amountTypeOptions,
      tagId: this.tagOptions
    };
    return map[key] || [];
  }

  getOptionValue(option: any): string {
    return String(option?.value ?? option?.id ?? option?.name ?? option?.label ?? '');
  }

  getOptionLabel(option: any): string {
    return String(option?.label ?? option?.value ?? option?.name ?? option?.id ?? '');
  }

  getMultiSelectValues(filterKey: string): Array<string> {
    const raw = this.filterModel[filterKey];
    if (Array.isArray(raw)) {
      return _.compact(raw.map((value: any) => String(value)));
    }
    if (raw === undefined || raw === null || raw === '') {
      return [];
    }
    return [String(raw)];
  }

  isOptionSelected(filterKey: string, option: any): boolean {
    const selectedValues = this.getMultiSelectValues(filterKey);
    const optionValue = this.getOptionValue(option);
    return selectedValues.includes(optionValue);
  }

  onMultiSelectOptionChange(filterKey: string, option: any, $event: any) {
    $event.stopImmediatePropagation();
    if (!this.isMultiSelectEnabled(filterKey)) {
      return;
    }
    const isChecked = !!$event.target?.checked;
    const optionValue = this.getOptionValue(option);
    const selectedValues = [...this.getMultiSelectValues(filterKey)];
    if (isChecked) {
      selectedValues.push(optionValue);
      this.filterModel[filterKey] = _.uniq(selectedValues);
    } else {
      this.filterModel[filterKey] = selectedValues.filter((value) => value !== optionValue);
    }
  }

  getSelectedOptions(filterKey: string): Array<{ value: string; label: string }> {
    const options = this.getOptionsByFilterKey(filterKey);
    const selectedValues = this.getMultiSelectValues(filterKey);
    return selectedValues.map((selectedValue) => {
      const selectedOption = _.find(options, (option) => this.getOptionValue(option) === selectedValue);
      return {
        value: selectedValue,
        label: selectedOption ? this.getOptionLabel(selectedOption) : selectedValue
      };
    });
  }

  // getSelectedOptions() returns new object literals on every change-detection run.
  // Without trackBy, *ngFor sees "different" objects each time and destroys/recreates
  // the chip DOM nodes (and their remove-button listeners), which is what makes the
  // X click miss or need repeated clicks. Tracking by value keeps the nodes stable.
  trackByFilterValue(index: number, item: { value: string; label: string }): string {
    return item.value;
  }

  removeMultiSelectValue(filterKey: string, value: string, $event?: any) {
    if ($event) {
      if (typeof $event.preventDefault === 'function') {
        $event.preventDefault();
      }
      if (typeof $event.stopImmediatePropagation === 'function') {
        $event.stopImmediatePropagation();
      } else {
        $event.stopPropagation();
      }
    }
    // Deliberately no isMultiSelectEnabled() gate here: that flag tracks whether
    // NEW selections should be offered for this filter (it can flip to false as
    // strategies move into the "Selected" list and shrink the available pool).
    // Removing an already-selected chip must always succeed regardless.
    const selectedValues = [...this.getMultiSelectValues(filterKey)];
    const normalizedValue = String(value ?? '').trim();
    this.filterModel[filterKey] = selectedValues
      .map((entry) => String(entry ?? '').trim())
      .filter((entry) => entry !== normalizedValue);
    this.filterData();
  }

  // AI-suggested items are slim projections (see formatedAiSuggestedStrategies
  // in dashboard-upload-create-plan) that exclude complexity/applicableTo/tags etc.
  // For filtering, resolve to the full template from `strategies` when available.
  private getFullStrategy(item: any): any {
    if (item && Array.isArray(this.strategies)) {
      const full = _.find(this.strategies, { id: item.id });
      if (full) return full;
    }
    return item || {};
  }

  private applyPropertyFilters(list: any[]): any[] {
    let result = list || [];

    const selectedCategories = this.getMultiSelectValues('category');
    if (selectedCategories.length && this.filterAvailability.category) {
      result = _.filter(result, (s: any) => selectedCategories.includes(String(this.getFullStrategy(s).type || '')));
    }
    const selectedComplexities = this.getMultiSelectValues('complexity');
    if (selectedComplexities.length && this.filterAvailability.complexity) {
      result = _.filter(result, (s: any) => selectedComplexities.includes(String(this.getProp(s, ['complexity']) || '')));
    }
    const selectedUrgencies = this.getMultiSelectValues('urgency');
    if (selectedUrgencies.length && this.filterAvailability.urgency) {
      result = _.filter(result, (s: any) => selectedUrgencies.includes(String(this.getProp(s, ['urgency']) || '')));
    }
    const selectedRisks = this.getMultiSelectValues('riskLevel');
    if (selectedRisks.length && this.filterAvailability.riskLevel) {
      result = _.filter(result, (s: any) => selectedRisks.includes(String(this.getProp(s, ['riskLevel', 'risk_level', 'risk']) || '')));
    }
    const selectedBurdens = this.getMultiSelectValues('burden');
    if (selectedBurdens.length && this.filterAvailability.burden) {
      result = _.filter(result, (s: any) => selectedBurdens.includes(String(this.getProp(s, ['burden']) || '')));
    }
    const selectedAmountTypes = this.getMultiSelectValues('amountType');
    if (selectedAmountTypes.length && this.filterAvailability.amountType) {
      result = _.filter(result, (s: any) => selectedAmountTypes.includes(String(this.getProp(s, ['amountType']) || '')));
    }
    if (this.filterModel.applicableTo && this.filterAvailability.applicableTo) {
      result = _.filter(result, (s: any) => this.getProp(s, ['applicableTo']) === this.filterModel.applicableTo);
    }
    if (this.filterModel.requiresInvestment && this.filterAvailability.requiresInvestment) {
      const want = this.filterModel.requiresInvestment === 'Yes';
      // Missing flag (both undefined) is treated as `false` here. This matches
      // the DB column `is_investment_enabled tinyint(1) DEFAULT 0` — an absent
      // value semantically means "does not require investment" and so flows
      // into the "No" bucket. The DEFAULT in the schema makes this case rare in
      // practice; it can only happen if getFullStrategy() can't resolve the id.
      result = _.filter(result, (s: any) => {
        const t = this.getFullStrategy(s);
        return !!(t.isInvestmentEnabled ?? t.investmentEnabled) === want;
      });
    }
    const selectedTagIds = this.getMultiSelectValues('tagId');
    if (selectedTagIds.length && this.filterAvailability.tags) {
      // Client-side tag filtering to work consistently across both AI Strategies and All Strategies tabs
      result = _.filter(result, (s: any) => {
        const strategy = this.getFullStrategy(s);
        return strategy.tags && strategy.tags.some((tag: any) => selectedTagIds.includes(String(tag.id)));
      });
    }
    return result;
  }

  // Safely read a property from the full strategy, supporting alternate field names.
  private getProp(item: any, keys: string[]): any {
    const strategy = this.getFullStrategy(item) || {};
    for (const key of keys) {
      if (strategy[key] !== undefined && strategy[key] !== null && strategy[key] !== '') {
        return strategy[key];
      }
    }
    return undefined;
  }

  drop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

    const intoSelected = event.container.data === this.selectedStrategies;
    const outOfSelected = event.previousContainer.data === this.selectedStrategies;

    if (intoSelected) {
      this.selectedStrategies = _.uniqBy(this.selectedStrategies, 'id');
      this.onItemAddedToSelectedList.emit({list: this.selectedStrategies});
    } else if (outOfSelected) {
      const moved = event.container.data[event.currentIndex];
      this.onItemAddedToSelectedList.emit({
        action: 'removed',
        item: moved,
        list: this.selectedStrategies
      });
    }

    // Re-source the non-selected list through current search/filter.
    this.filterData();
  }

  filterData() {
    let result: any[];

    if (!this.searchKey){
      const data = (this.currentTab === 'AI Strategies') ? _.cloneDeep(this.aiSuggestedStrategies) : _.cloneDeep(this.strategies);
      _.each(this.selectedStrategies, (strategy) => {
        let obj = _.find(data, {id: strategy.id});
        if (obj) {
          _.remove(data, {id: strategy.id});
        }
      });
      result = data || [];
    } else {
      const base = this;
      const sourceList = (this.currentTab === 'AI Strategies')
        ? _.cloneDeep(this.aiSuggestedStrategies)
        : _.cloneDeep(this.strategies);
      _.each(this.selectedStrategies, (strategy) => {
        if (_.find(sourceList, {id: strategy.id})) {
          _.remove(sourceList, {id: strategy.id});
        }
      });
      const filteredList = [];
      _.each(sourceList, (strategy) => {
        if (strategy.name && strategy.name.toLowerCase().indexOf(base.searchKey.toLowerCase()) != -1){
          if (!_.find(filteredList, {id: strategy.id})){
            filteredList.push(strategy);
          }
        }
      });
      result = filteredList;
    }

    result = this.applyPropertyFilters(result);

    if (this.currentTab === 'AI Strategies') {
      this.aiStrategies = result;
    } else {
      this.allStrategiesData = result;
    }

    setTimeout(() => this.initTooltips());
  }

  toggleDescription(item) {
    item.showFullDescription = !item.showFullDescription;
  }

  getTooltipContent(label: string, info?: string): string {
    let content = `<div>${label}</div>`;
    if (info) {
      content += `<div>${info}</div>`;
    }
    return content;
  }


}

